import { useState, useEffect } from 'react';
import { FilterSelect } from '../common/FilterSelect';

export interface PhotoFiltersState {
  search: string;
  /** `EventType` ids — the website section a photograph is filed under. */
  eventTypeIds: string[];
  eventIds: string[];
  venueIds: string[];
  vendorIds: string[];
  userIds: string[];
  /** 'public' | 'landing' | 'private', ORed together. */
  publishStates: string[];
}

interface PhotoFiltersProps {
  filters: PhotoFiltersState;
  onFiltersChange: (filters: PhotoFiltersState) => void;
  /** The office's own categories, from Settings → Event Types. */
  eventTypes?: { id: string; name: string }[];
  events?: { id: string; name: string }[];
  venues?: { id: string; name: string }[];
  vendors?: { id: string; businessName: string }[];
  users?: { id: string; name: string }[];
}

const PUBLISH_STATE_OPTIONS = [
  { id: 'public', label: 'Public' },
  { id: 'landing', label: 'Featured on landing page' },
  { id: 'private', label: 'Private' },
];

/**
 * Every facet is independent and they AND together — picking a vendor and a
 * category narrows to photos tagged with both, not either — while several
 * picks inside one facet OR (any of the selected vendors). Publish state is
 * its own facet, so it combines with the tag facets rather than fighting
 * them the way the old single entityType/eventTypeId pair did.
 */
export function PhotoFilters({
  filters,
  onFiltersChange,
  eventTypes = [],
  events = [],
  venues = [],
  vendors = [],
  users = [],
}: PhotoFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFiltersChange({ ...filters, search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters, onFiltersChange]);

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <input
        type="text"
        placeholder="Search by caption..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
      />
      {eventTypes.length > 0 && (
        <FilterSelect
          label="Category"
          items={eventTypes}
          value={filters.eventTypeIds}
          onChange={(value) => onFiltersChange({ ...filters, eventTypeIds: value as string[] })}
          getValue={(t) => t.id}
          getLabel={(t) => t.name}
          multiple
        />
      )}
      <FilterSelect
        label="Event"
        items={events}
        value={filters.eventIds}
        onChange={(value) => onFiltersChange({ ...filters, eventIds: value as string[] })}
        getValue={(e) => e.id}
        getLabel={(e) => e.name}
        multiple
      />
      <FilterSelect
        label="Venue"
        items={venues}
        value={filters.venueIds}
        onChange={(value) => onFiltersChange({ ...filters, venueIds: value as string[] })}
        getValue={(v) => v.id}
        getLabel={(v) => v.name}
        multiple
      />
      <FilterSelect
        label="Vendor"
        items={vendors}
        value={filters.vendorIds}
        onChange={(value) => onFiltersChange({ ...filters, vendorIds: value as string[] })}
        getValue={(v) => v.id}
        getLabel={(v) => v.businessName}
        multiple
      />
      <FilterSelect
        label="User"
        items={users}
        value={filters.userIds}
        onChange={(value) => onFiltersChange({ ...filters, userIds: value as string[] })}
        getValue={(u) => u.id}
        getLabel={(u) => u.name}
        multiple
      />
      <FilterSelect
        label="Publish state"
        items={PUBLISH_STATE_OPTIONS}
        value={filters.publishStates}
        onChange={(value) => onFiltersChange({ ...filters, publishStates: value as string[] })}
        getValue={(o) => o.id}
        getLabel={(o) => o.label}
        multiple
        searchable={false}
      />
    </div>
  );
}
